/**
 * sedDependencyGraph — the ONE composed dependency graph the cost strategy
 * (`docs/architecture/sed-report-cost-strategy.md` §2a) found missing.
 *
 * Three input-class registries already exist in-repo but are never JOINED:
 *
 *   - **providers**       ← `SED_SECTION_PROVIDERS` (`sedStaleness.ts`)
 *   - **docRequirements** ← reverse of `REQUIREMENT_TO_SECTION_MAP`
 *                            (`requirementToSection.ts`)
 *   - **intentFields**    ← NEW, hand-authored: which `ProjectIntent`
 *                            dot-paths CONDITION each section. Derived from
 *                            `REQUIREMENT_FIELD_ALIGNMENT`'s setup/intent
 *                            attestation rows where they map to a section,
 *                            plus the intent-stamped sections (technology).
 *   - **upstreamSections** ← NEW, hand-authored: the cross-section edges the
 *                            strategy documents. These encode the
 *                            "naive incrementalism is unsafe" risk: refreshing
 *                            one section legitimately requires re-deriving
 *                            others (regulatory derives from electricity
 *                            coords; externalLiterature seed terms derive from
 *                            regulatory + waterStress; publicSentiment locality
 *                            derives from regulatory).
 *
 * This composed graph is the substrate `computeSedDirtySet` (sedFingerprint.ts)
 * walks to decide which sections to recompute on a rebuild.
 *
 * INVARIANT (test-enforced, mirrors `requirementToFields.test.ts`): EVERY
 * `SedSectionId` MUST appear as a key. That closes the strategy-flagged gap —
 * "a new report section can ship with zero declared input dependencies and
 * silently never be marked stale." A missing key is a compile-time + test
 * failure, never a silent stale section.
 */
import type { SedSectionId } from '../../types/siteEvidence';
import { SED_SECTION_IDS } from '../../types/siteEvidence';
import { SED_SECTION_PROVIDERS } from '../sedStaleness';
import { REQUIREMENT_TO_SECTION_MAP } from '../../config/requirementToSection';

/**
 * Per-section dependency declaration. The four axes are the four classes of
 * input whose change can invalidate a section's compiled evidence.
 */
export interface SedSectionDeps {
  /** The section this declaration is for. */
  section: SedSectionId;
  /**
   * Live-provider ids that feed this section (from `SED_SECTION_PROVIDERS`).
   * Empty for derived / intent-stamped / KMZ sections — they have no live
   * provider, so their fingerprint is conditioned by intentFields / docs /
   * upstreamSections instead.
   */
  providers: string[];
  /**
   * `ProjectIntent` dot-paths that CONDITION this section. A change to any
   * of these intent values must re-fingerprint (and so re-derive) the
   * section. Hand-authored — see the strategy's intent-stamping note: e.g.
   * `technology` is intent-stamped only (`providers:[]`,
   * `intentFields:['nuclearInterest','reactorDesignClass',…]`).
   */
  intentFields: string[];
  /**
   * Checklist-requirement ids whose linked documents feed this section
   * (reverse of `REQUIREMENT_TO_SECTION_MAP`). A change to ANY doc linked
   * via one of these requirements re-fingerprints the section.
   */
  docRequirements: string[];
  /**
   * Cross-section edges — sections THIS section derives FROM. When an
   * upstream section's fingerprint changes, this section is transitively
   * dirty (BFS closure in `computeSedDirtySet`). Hand-authored to match the
   * documented cross-section derivations. MUST be acyclic.
   */
  upstreamSections: SedSectionId[];
}

/**
 * Hand-authored intent-field conditioning per section.
 *
 * Derivation rationale (kept explicit so the edges are auditable, not magic):
 *
 *  - `technology` is INTENT-STAMPED — it has no live provider; the reactor
 *    design class comes straight from ProjectIntent. Its inputs ARE these
 *    intent fields.
 *  - `electricity` / `grid` carry the power-availability setup knobs the
 *    report-setup alignment rows map (btmStrategy / power requirement etc.),
 *    plus the power-stack basis that scales the demand figure.
 *  - `waterStress` is conditioned by the water-resource setup + the cooling
 *    axes (dry/ZLD vs wet cooling changes withdrawal — see reportPrompts
 *    ZLD/dry-cooling tradeoff).
 *  - `environmental` / `regulatory` carry the environmental setup knobs
 *    (NEPA pathway, federal nexus, tribal proximity).
 *  - `connectivity` carries the interconnection setup knobs.
 *  - `parcel` / `siteProfile` carry the test-fit footprint/phasing knobs.
 *
 * Sections with no intent conditioning map to `[]`.
 */
const SECTION_INTENT_FIELDS: Record<SedSectionId, string[]> = {
  climate: [],
  // Cooling axes change the water-withdrawal basis (wet vs dry/ZLD).
  waterStress: [
    'coolingStrategy',
    'bopHeatRejection',
    'coolingGeneration',
    'pueValue',
    'pueTarget',
  ],
  seismic: [],
  geotechnical: [],
  // Power demand + BTM strategy condition the grid-availability framing.
  grid: [
    'powerRequirement',
    'powerRequirementMW',
    'powerBasis',
    'pueValue',
    'powerFactor',
    'timeline',
    'inServiceDate',
  ],
  flood: [],
  // Environmental posture is conditioned by the carbon commitment + the
  // environmental-report-setup intent knobs.
  environmental: ['carbonCommitment'],
  nuclearInfra: ['nuclearInterest', 'reactorDesignClass', 'reactorVendorId'],
  // Renewables preference conditions how BTM solar is framed.
  solar: ['carbonCommitment'],
  wind: ['carbonCommitment'],
  // Electricity / tariff framing is conditioned by the power-stack basis and
  // the power requirement.
  electricity: [
    'powerRequirement',
    'powerRequirementMW',
    'powerBasis',
    'pueValue',
    'priority',
  ],
  airQuality: [],
  connectivity: ['workloadProfile'],
  parcel: [],
  // Regulatory posture is conditioned by carbon commitment + nuclear interest
  // (a nuclear path changes the regulatory framing materially).
  regulatory: ['nuclearInterest', 'carbonCommitment'],
  siteResearch: [],
  adamsDocuments: ['nuclearInterest'],
  // Site profile is conditioned by the test-fit footprint / phasing knobs.
  siteProfile: ['phases'],
  onSiteInfrastructure: [],
  externalLiterature: [],
  publicSentiment: [],
  // INTENT-STAMPED: technology IS the reactor design class from intent.
  technology: ['nuclearInterest', 'reactorDesignClass', 'reactorVendorId'],
  // v1.2.1 HR-5 — INTENT-STAMPED BTM grounding sections. Each derives from the
  // intent fields it projects, so a change to those fields dirties the section.
  cooling: [
    'coolingStrategy',
    'bopHeatRejection',
    'coolingGeneration',
    'pueValue',
    'pueTarget',
    'wasteHeatOfftake',
  ],
  // `powerRequirement` (the coarse BUCKET) belongs here alongside the exact MW:
  // a bucket-only intent now grounds `power` as a bucket-derived BAND, so
  // changing the bucket must dirty the section. Omitting it meant switching
  // 200-500mw → 500-1000mw left the SED serving a stale band.
  // (`grid` and `electricity` below already list it.)
  power: ['powerRequirementMW', 'powerRequirement', 'powerBasis', 'pueValue', 'powerFactor', 'phases'],
  firmCapacity: ['capacityCreditIntent'],
  // lcoe / fuelSupply derive from financialAssumptions + the gas power mix
  // (phases) rather than the coarse intent knobs above.
  lcoe: ['phases'],
  fuelSupply: ['phases'],
  // SED D2 — interconnection is conditioned by the declared interconnection
  // queue status + the requested power (phases). incentives derives from the
  // financial assumptions (technology / in-service / capacity), which the
  // phases roll up — so a phasing change dirties the IRA eligibility framing.
  interconnection: ['interconnectionStatus', 'powerRequirementMW', 'phases'],
  incentives: ['phases'],
  // SED D2 (H-6) — multiHazard is purely location-derived (FEMA NRI on lat/lng);
  // transmissionHeadroom + waterResource are honest-unavailable typed sections
  // today. None is conditioned on a ProjectIntent field, so no intent edge
  // dirties them (a site relocation re-stamps them via the bootstrap stamp).
  multiHazard: [],
  transmissionHeadroom: [],
  waterResource: [],
  topography: [],
  survey: [],
  mineralResources: [],
  culturalResources: [],
  // Server-authored economic snapshots bind the current project sizing and
  // operating assumptions. Snapshot revision CAS is the publication guard;
  // these edges also make intent edits visibly dirty the section immediately.
  economicEvidence: [
    'powerRequirement',
    'powerRequirementMW',
    'powerBasis',
    'phases',
    'workloadProfile',
    'coolingStrategy',
  ],
};

/**
 * Hand-authored cross-section edges. `upstreamSections[S]` lists the sections
 * S DERIVES FROM — a change upstream transitively dirties S.
 *
 * These mirror the documented derivations (strategy §2a):
 *  - `regulatory` ← `electricity` : `inferSiteStateFromSed` /
 *    `applyDocAwareConfidence` derive coords/state from the electricity slot.
 *  - `externalLiterature` ← `regulatory`, `waterStress` : live-literature seed
 *    terms derive from the regulatory + water-stress context.
 *  - `publicSentiment` ← `regulatory` : locality (state/location) for the
 *    GDELT / council-minutes / docket queries derives from regulatory.
 *  - `adamsDocuments` ← `nuclearInfra` : ADAMS docket scope follows the
 *    nearest-reactor context.
 *
 * MUST be acyclic (test-enforced) so the BFS closure terminates.
 */
const SECTION_UPSTREAM: Partial<Record<SedSectionId, SedSectionId[]>> = {
  regulatory: ['electricity'],
  externalLiterature: ['regulatory', 'waterStress'],
  publicSentiment: ['regulatory'],
  adamsDocuments: ['nuclearInfra'],
};

/**
 * Reverse `REQUIREMENT_TO_SECTION_MAP` once at module load: section → the
 * checklist-requirement ids whose linked docs feed it. This is the
 * `requirementsForSection` reverse lookup, materialised as a map so the
 * dependency graph build is O(1) per section.
 */
function buildSectionToRequirements(): Record<SedSectionId, string[]> {
  const out = {} as Record<SedSectionId, string[]>;
  for (const id of SED_SECTION_IDS) out[id] = [];
  for (const [reqId, sections] of Object.entries(REQUIREMENT_TO_SECTION_MAP)) {
    for (const sectionId of sections) {
      // Defensive: only index sections that are canonical ids.
      if (sectionId in out) out[sectionId as SedSectionId].push(reqId);
    }
  }
  // Stable order for deterministic fingerprints downstream.
  for (const id of SED_SECTION_IDS) out[id].sort();
  return out;
}

const SECTION_TO_REQUIREMENTS = buildSectionToRequirements();

/**
 * The composed dependency graph: one `SedSectionDeps` per `SedSectionId`.
 * Built once at module load by joining the three registries + the two
 * hand-authored axes. Frozen so no caller can mutate the shared graph.
 */
export const SED_DEPENDENCY_GRAPH: Record<SedSectionId, SedSectionDeps> = (() => {
  const graph = {} as Record<SedSectionId, SedSectionDeps>;
  for (const section of SED_SECTION_IDS) {
    graph[section] = Object.freeze({
      section,
      providers: [...(SED_SECTION_PROVIDERS[section] ?? [])],
      intentFields: [...(SECTION_INTENT_FIELDS[section] ?? [])],
      docRequirements: [...(SECTION_TO_REQUIREMENTS[section] ?? [])],
      upstreamSections: [...(SECTION_UPSTREAM[section] ?? [])],
    });
  }
  return Object.freeze(graph);
})();

/** Accessor — the dependency declaration for a section. */
export function depsForSection(section: SedSectionId): SedSectionDeps {
  return SED_DEPENDENCY_GRAPH[section];
}

/* ── Recompute mechanism classification (P4 under-recompute fix) ─────────
 *
 * The incremental rebuild must GENUINELY recompute every dirty section, by the
 * SAME producer the FULL rebuild uses for that section. Different section TYPES
 * have different producers:
 *
 *   - `provider-fed`    — a live deep/fast provider fills the slot
 *                         (`topUpSiteSed` provider sweep). e.g. climate, grid.
 *   - `intent-stamped`  — derived purely from ProjectIntent with NO live
 *                         provider; the full rebuild runs `stampTechnologyFromIntent`.
 *                         e.g. `technology`. The REAL `topUpSiteSed` does NOT
 *                         touch these (it filters on a provider id), so the
 *                         incremental path MUST invoke the intent producer
 *                         itself — otherwise the section is stamped-current
 *                         without being recomputed (the bug this fixes).
 *   - `orchestrated`    — a synthetic orchestrator provider fills the slot
 *                         (`external-literature`, `public-sentiment`).
 *                         `topUpSiteSed` recomputes these via its own
 *                         orchestrator branches when the section is in
 *                         `requiredSections`.
 *   - `derived`         — computed locally from other SED state / KMZ / docs
 *                         with no dedicated incremental producer wired yet
 *                         (`parcel`, `regulatory`, `adamsDocuments`,
 *                         `siteProfile`, `onSiteInfrastructure`). The
 *                         incremental harness CANNOT genuinely recompute these
 *                         today → if one is dirty we FORCE A FULL REBUILD
 *                         rather than stamp-without-recompute.
 *
 * CARDINAL RULE: a section may be stamped current ONLY by a mechanism that
 * actually recomputed it. `derived` has no incremental mechanism, so a dirty
 * `derived` section is the trigger to fall back to the full rebuild.
 */
export type SectionRecomputeMechanism =
  | 'provider-fed'
  | 'intent-stamped'
  | 'orchestrated'
  | 'derived';

/**
 * Sections that are stamped purely from ProjectIntent (no live provider). The
 * full rebuild runs `stampTechnologyFromIntent` for these; the incremental path
 * must run the SAME producer for the dirty subset.
 */
const INTENT_STAMPED_SECTIONS: ReadonlySet<SedSectionId> = new Set<SedSectionId>([
  'technology',
  // v1.2.1 HR-5 — cooling / power / firmCapacity are intent-stamped from the
  // Project Intent (via `stampBtmGroundingFromIntent`). `lcoe` is also routed
  // through the intent stamper: it re-derives from the merged SED's
  // financialAssumptions + fuelSupply (already on the SED) via `buildLcoeEvidence`,
  // so it doesn't need a live provider fetch. (`fuelSupply` IS provider-fed —
  // see TOPUP_FETCHABLE_SECTIONS — because it needs the EIA natural-gas price.)
  'cooling',
  'power',
  'firmCapacity',
  'lcoe',
]);

/** Synthetic orchestrator-backed sections — `topUpSiteSed` recomputes these. */
const ORCHESTRATED_SECTIONS: ReadonlySet<SedSectionId> = new Set<SedSectionId>([
  'externalLiterature',
  'publicSentiment',
]);

/**
 * Sections `topUpSiteSed` can GENUINELY recompute via a live (fetchable)
 * provider in its SCOPED (`onlyProviders`) mode — i.e. the providers wired into
 * `fetchAllSiteData` (`dataProviders/index.ts` switch).
 *
 * NOTE — this is INTENTIONALLY broader than `SED_SECTION_PROVIDERS`
 * (`sedStaleness.ts`), which only lists a section's PRIMARY live provider and
 * marks `parcel` / `regulatory` / `adamsDocuments` / `siteProfile` as
 * `[]` ("derived; no live provider"). Those four ARE fetchable via
 * `regrid-parcels` / `regulatory-db` / `nrc-adams` / `site-profile` (real cases
 * in the `fetchAllSiteData` switch), so `topUpSiteSed`'s scoped mode CAN
 * recompute them. If the classifier keyed off `SED_SECTION_PROVIDERS` it would
 * mis-classify these four as `derived` and force a full rebuild on every
 * reactor-field edit (which transitively dirties `adamsDocuments`) — defeating
 * the cost win. The canonical mapping here mirrors `SECTION_TO_PROVIDER_ID` in
 * `sedBootstrap.ts` (topUp's own view), minus the non-fetch synthetic ids
 * (`kmz-derived`, `project-intent`) and the orchestrator ids (handled above).
 *
 * A section NOT in this set, not intent-stamped, and not orchestrated is
 * genuinely `derived` (today: `onSiteInfrastructure`, KMZ-derived from
 * `site.siteLayers` with no fetch path) ⇒ a dirty one forces a full rebuild.
 */
const TOPUP_FETCHABLE_SECTIONS: ReadonlySet<SedSectionId> = new Set<SedSectionId>([
  'climate',        // open-meteo
  'seismic',        // usgs-seismic
  'flood',          // fema-flood
  'grid',           // hifld-grid
  'environmental',  // usfws-env
  'waterStress',    // wri-aqueduct
  'electricity',    // eia-electricity
  'airQuality',     // airnow-aqi
  'solar',          // nrel-solar
  'wind',           // nrel-wind
  'nuclearInfra',   // nrc-reactors
  'connectivity',   // peeringdb-ix
  'siteResearch',   // site-research
  'parcel',         // regrid-parcels
  'regulatory',     // regulatory-db
  'adamsDocuments', // nrc-adams
  'siteProfile',    // site-profile
  'fuelSupply',     // eia-natural-gas (v1.2.1 HR-5 P2-4)
]);

/**
 * Provider id `topUpSiteSed` runs (in `onlyProviders` mode) to recompute a
 * given fetchable / orchestrated section. Mirrors `SECTION_TO_PROVIDER_ID` in
 * `sedBootstrap.ts` for exactly the sections the incremental path drives.
 * Returns `undefined` for sections topUp cannot fetch (intent-stamped /
 * derived) — callers must not pass those to `onlyProviders`.
 */
const SECTION_TOPUP_PROVIDER_ID: Partial<Record<SedSectionId, string>> = {
  climate: 'open-meteo',
  seismic: 'usgs-seismic',
  flood: 'fema-flood',
  grid: 'hifld-grid',
  environmental: 'usfws-env',
  waterStress: 'wri-aqueduct',
  electricity: 'eia-electricity',
  airQuality: 'airnow-aqi',
  solar: 'nrel-solar',
  wind: 'nrel-wind',
  nuclearInfra: 'nrc-reactors',
  connectivity: 'peeringdb-ix',
  siteResearch: 'site-research',
  parcel: 'regrid-parcels',
  regulatory: 'regulatory-db',
  adamsDocuments: 'nrc-adams',
  siteProfile: 'site-profile',
  externalLiterature: 'external-literature',
  publicSentiment: 'public-sentiment',
  fuelSupply: 'eia-natural-gas', // v1.2.1 HR-5 P2-4
};

/**
 * Classify a section's recompute mechanism — which producer the incremental
 * path must invoke to GENUINELY recompute it. Mirrors the full rebuild:
 *   - intent-stamped first (a providerless intent-only section),
 *   - then orchestrated (synthetic providers `topUpSiteSed` handles),
 *   - then provider-fed (a live provider topUp can fetch in scoped mode),
 *   - else derived (no incremental producer wired ⇒ force-full).
 */
export function recomputeMechanismFor(section: SedSectionId): SectionRecomputeMechanism {
  if (INTENT_STAMPED_SECTIONS.has(section)) return 'intent-stamped';
  if (ORCHESTRATED_SECTIONS.has(section)) return 'orchestrated';
  if (TOPUP_FETCHABLE_SECTIONS.has(section)) return 'provider-fed';
  return 'derived';
}

/**
 * True when a section is filled by a live (fetchable) provider — i.e. its data
 * comes from a provider sweep, not from intent-stamping, an orchestrator, or
 * local KMZ derivation. The incremental rebuild's null-data guard uses this to
 * decide which sections to inspect for an EMPTY slot: a provider-fed section is
 * the only kind whose `.data` can legitimately be re-fetched by the scoped
 * topUp, so an empty provider slot is the actionable data-starvation case.
 *
 * NOTE — this is the `'provider-fed'` arm of `recomputeMechanismFor`, exposed as
 * a predicate so the dirty-set computer (`sedFingerprint.ts`) does not have to
 * re-derive the classification.
 */
export function isProviderFedSection(section: SedSectionId): boolean {
  return recomputeMechanismFor(section) === 'provider-fed';
}

/**
 * The provider ids the scoped (`onlyProviders`) topUp must run to recompute the
 * given provider-fed / orchestrated sections. Use this (NOT
 * `providersForDirtySections`, which reads the narrower `SED_SECTION_PROVIDERS`)
 * for the incremental recompute so `parcel` / `regulatory` / `adamsDocuments` /
 * `siteProfile` actually get fetched.
 */
export function topUpProvidersForSections(
  sections: ReadonlyArray<SedSectionId>,
): string[] {
  const set = new Set<string>();
  for (const sec of sections) {
    const pid = SECTION_TOPUP_PROVIDER_ID[sec];
    if (pid) set.add(pid);
  }
  return [...set];
}

/**
 * Reverse adjacency: section → sections that list it as an upstream
 * dependency (its DOWNSTREAM dependents). Used by `computeSedDirtySet`'s
 * transitive closure — when S is dirty, every section that derives FROM S
 * is also dirty.
 */
export const SED_DOWNSTREAM_DEPENDENTS: Record<SedSectionId, SedSectionId[]> = (() => {
  const out = {} as Record<SedSectionId, SedSectionId[]>;
  for (const id of SED_SECTION_IDS) out[id] = [];
  for (const section of SED_SECTION_IDS) {
    for (const upstream of SED_DEPENDENCY_GRAPH[section].upstreamSections) {
      out[upstream].push(section);
    }
  }
  for (const id of SED_SECTION_IDS) out[id].sort();
  return Object.freeze(out);
})();

/**
 * Map a set of dirty sections to the de-duplicated provider id list a scoped
 * `topUpSiteSed` should run. Mirrors `providersForSections` in
 * `sedStaleness.ts` but keyed directly off the composed graph.
 */
export function providersForDirtySections(
  dirty: ReadonlyArray<SedSectionId>,
): string[] {
  const providers = new Set<string>();
  for (const section of dirty) {
    for (const p of SED_DEPENDENCY_GRAPH[section].providers) providers.add(p);
  }
  return [...providers];
}

/**
 * Test/diagnostic helper — assert the graph is exhaustive and acyclic. Returns
 * a list of problems (empty when healthy). Exported so the unit test can pin
 * the invariant the way `findFieldRegistryInconsistencies` does for the
 * requirement registries.
 */
export interface GraphProblem {
  kind: 'missing-section' | 'unknown-upstream' | 'cycle' | 'self-edge';
  section: SedSectionId;
  detail?: string;
}

export function findDependencyGraphProblems(): GraphProblem[] {
  const problems: GraphProblem[] = [];
  const known = new Set<string>(SED_SECTION_IDS);

  // 1. Exhaustiveness — every section id is a key.
  for (const id of SED_SECTION_IDS) {
    if (!(id in SED_DEPENDENCY_GRAPH)) {
      problems.push({ kind: 'missing-section', section: id });
    }
  }

  // 2. Upstream edges point at known sections, and no self-edges.
  for (const section of SED_SECTION_IDS) {
    for (const up of SED_DEPENDENCY_GRAPH[section].upstreamSections) {
      if (!known.has(up)) {
        problems.push({ kind: 'unknown-upstream', section, detail: up });
      }
      if (up === section) {
        problems.push({ kind: 'self-edge', section, detail: up });
      }
    }
  }

  // 3. Acyclicity — DFS with a recursion stack.
  const WHITE = 0, GREY = 1, BLACK = 2;
  const colour = new Map<SedSectionId, number>();
  for (const id of SED_SECTION_IDS) colour.set(id, WHITE);

  const visit = (node: SedSectionId): void => {
    colour.set(node, GREY);
    for (const up of SED_DEPENDENCY_GRAPH[node].upstreamSections) {
      if (!known.has(up)) continue;
      const c = colour.get(up);
      if (c === GREY) {
        problems.push({ kind: 'cycle', section: node, detail: `→ ${up}` });
      } else if (c === WHITE) {
        visit(up);
      }
    }
    colour.set(node, BLACK);
  };

  for (const id of SED_SECTION_IDS) {
    if (colour.get(id) === WHITE) visit(id);
  }

  return problems;
}
